import React from "react";
import { Badge, Group, NavLink, Stack, Text } from "@mantine/core";
import { IconBuilding } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { OrganisationNode } from "@models/access";
import { isInactive } from "./organisations";

interface OrganisationTreeProps {
  roots: OrganisationNode[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const Node: React.FC<{ node: OrganisationNode; selectedId: string | null; onSelect: (id: string) => void }> = ({
  node, selectedId, onSelect,
}) => {
  const { t } = useTranslation();
  // A deactivated organisation stays in BeeHive (and in the tree): marked, not hidden.
  const label = isInactive(node) ? (
    <Group gap="xs" wrap="nowrap">
      <Text size="sm" c="dimmed" truncate>{node.name}</Text>
      <Badge size="xs" variant="outline" color="gray">{t("admin.access.organisations.inactive")}</Badge>
    </Group>
  ) : node.name;
  return (
  <NavLink
    label={label}
    leftSection={<IconBuilding size={16} />}
    active={node.id === selectedId}
    onClick={() => onSelect(node.id)}
    defaultOpened
    childrenOffset={16}
    aria-current={node.id === selectedId ? "true" : undefined}
  >
    {node.children.length > 0
      ? node.children.map((child) => <Node key={child.id} node={child} selectedId={selectedId} onSelect={onSelect} />)
      : undefined}
  </NavLink>
  );
};

/** The DSML organisation tree (the scope), every level open, one selected. */
const OrganisationTree: React.FC<OrganisationTreeProps> = ({ roots, selectedId, onSelect }) => (
  <Stack gap={0}>
    {roots.map((root) => <Node key={root.id} node={root} selectedId={selectedId} onSelect={onSelect} />)}
  </Stack>
);

export default OrganisationTree;
